import { t, type StaffKey, type StaffLanguage } from './staffText'
import { storedRoleLabel } from './roleNames'
import type { AuditEvent } from './types'

/** The desk's own rows: their detail is said in words, and the desk is already named in the sentence. */
const DESK_ACTIONS = new Set([
  'desk.request', 'desk.request_cancel', 'desk.request_approve', 'desk.request_decline', 'desk.break_end', 'desk.self_close', 'counter.freed',
])

const KINDS: Record<string, StaffKey> = {
  lunch: 'counter.kind.lunch',
  short_break: 'counter.kind.short_break',
  task: 'counter.kind.task',
  close: 'counter.closeDesk',
}

const STATES: Record<string, StaffKey> = {
  pending: 'counter.pending',
  approved: 'audit.state.approved',
  declined: 'audit.state.declined',
  cancelled: 'audit.state.cancelled',
  auto: 'audit.state.auto',
}

const render = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** One desk detail as a label and a value; null leaves it out (an identifier). */
function deskFact(key: string, value: unknown, lang: StaffLanguage): { label: string; value: string } | null {
  const said = (k: StaffKey | undefined) => (k ? t(k, lang) : render(value))
  switch (key) {
    case 'kind':
      return { label: t('audit.detail.kind', lang), value: said(KINDS[String(value)]) }
    case 'state':
      return { label: t('audit.detail.state', lang), value: said(STATES[String(value)]) }
    case 'breakStarted':
      return { label: t('audit.detail.breakStarted', lang), value: t(value ? 'common.yes' : 'common.no', lang) }
    case 'minutes':
      return { label: t('audit.detail.minutes', lang), value: t('counter.minutes', lang, { n: render(value) }) }
    case 'reason':
      return { label: t('audit.detail.reason', lang), value: render(value) }
    case 'staffName':
      return { label: t('audit.detail.person', lang), value: render(value) }
  }
  return /Id$/.test(key) ? null : { label: key, value: render(value) }
}

const BOOKING_ACTIONS = new Set(['appointment.saved', 'appointment.cancelled', 'booking.found'])

/** Outcomes a booking client's sentence already says (Audit.tsx actionKey). */
const SAID = new Set(['booked', 'confirmed', 'moved'])

const SEARCHES: Record<string, StaffKey> = { code: 'audit.search.code', contact: 'audit.search.contact' }

const OUTCOMES: Record<string, StaffKey> = {
  booked: 'audit.booking.booked',
  'booked at the desk': 'audit.booking.booked',
  confirmed: 'audit.booking.confirmed',
  moved: 'audit.booking.moved',
  created: 'audit.booking.created',
  updated: 'audit.booking.updated',
  unchanged: 'audit.booking.unchanged',
}

const CHANNELS: Record<string, StaffKey> = {
  key: 'audit.channel.key',
  test: 'booking.testPage',
  link: 'audit.actor.manageLink',
  reception: 'booking.source.reception',
}

const MODULE_ACTIONS = new Set(['module.assign', 'module.unassign'])

const MODULES: Record<string, StaffKey> = {
  appointments: 'org.br.module.appointments',
  analytics: 'org.br.module.analytics',
  signage: 'org.br.module.signage',
}

type Row = { label: string; value: string }

/** A booking row's detail: what happened unless the sentence says it, a search, the channel, and the key. */
function bookingFacts(e: Pick<AuditEvent, 'detail'> & { actorLabel?: string; actorRole?: string }, lang: StaffLanguage): Row[] {
  const d = e.detail ?? {}
  const said = (keys: Record<string, StaffKey>, v: unknown) => (keys[String(v)] ? t(keys[String(v)]!, lang) : render(v))
  const rows: Row[] = []
  if (d.outcome !== undefined && !(e.actorRole === 'booking_client' && SAID.has(String(d.outcome)))) {
    rows.push({ label: t('audit.detail.booking', lang), value: said(OUTCOMES, d.outcome) })
  }
  if (d.by !== undefined) rows.push({ label: t('audit.detail.searchedBy', lang), value: said(SEARCHES, d.by) })
  if (d.found !== undefined) rows.push({ label: t('audit.detail.found', lang), value: render(d.found) })
  if (d.via !== undefined) rows.push({ label: t('audit.detail.channel', lang), value: said(CHANNELS, d.via) })
  if (d.via === 'key' && e.actorLabel) rows.push({ label: t('audit.detail.key', lang), value: e.actorLabel })
  return rows
}

/** The detail rows an entry opens to. */
export function detailRows(
  e: Pick<AuditEvent, 'action' | 'detail'> & { actorLabel?: string; actorRole?: string },
  lang: StaffLanguage,
): Row[] {
  if (BOOKING_ACTIONS.has(e.action)) return bookingFacts(e, lang)
  if (MODULE_ACTIONS.has(e.action)) {
    const m = e.detail?.module
    const key = MODULES[String(m)]
    return m === undefined ? [] : [{ label: t('audit.detail.module', lang), value: key ? t(key, lang) : render(m) }]
  }
  const entries = Object.entries(e.detail ?? {})
  if (!DESK_ACTIONS.has(e.action)) return entries.map(([label, value]) => ({ label, value: render(value) }))
  return entries.map(([k, v]) => deskFact(k, v, lang)).filter((f) => f !== null)
}

/** What the entry was applied to, by kind and identifier; empty for a desk, which the sentence names, and a booking. */
export function appliedTo(e: Pick<AuditEvent, 'action' | 'targetKind' | 'targetId'>): string {
  if (!e.targetKind || DESK_ACTIONS.has(e.action) || MODULE_ACTIONS.has(e.action) || e.targetKind === 'appointment') return ''
  return `${e.targetKind} ${e.targetId ?? ''}`.trim()
}

/** The actor's role on a row: a booking system in words, else as roleNames says it. */
export function roleText(stored: string, lang: StaffLanguage): string {
  return stored === 'booking_client' ? t('audit.role.bookingClient', lang) : storedRoleLabel(stored, lang)
}

export function outcomeText(outcome: AuditEvent['outcome'], lang: StaffLanguage): string {
  return t(outcome === 'allowed' ? 'audit.allowed' : outcome === 'denied' ? 'audit.refused' : 'audit.failed', lang)
}

/** "MCP/API token (name)" for an event a personal access token made; empty otherwise. */
export function viaTokenText(e: Pick<AuditEvent, 'viaToken'>, lang: StaffLanguage): string {
  return e.viaToken ? t('tokens.audit.viaToken', lang, { name: e.viaToken.name }) : ''
}
